import { useEffect, useRef, useState } from "react";

const useRunOnVisible = (
    callback: () => unknown,
    runOnce = true,
    options?: IntersectionObserverInit,
) => {
    const ref = useRef<HTMLDivElement>(null!);
    const [hasBeenInView, setHasBeenInView] = useState(false);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        if (hasBeenInView && runOnce) callback();
    }, [hasBeenInView, runOnce, callback]);

    useEffect(() => {
        if (isInView && !runOnce) callback();
    }, [isInView, runOnce, callback]);

    const root = options?.root,
        rootMargin = options?.rootMargin,
        threshold = options?.threshold ?? 0.1;

    useEffect(() => {
        const elem = ref.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        if (!hasBeenInView) setHasBeenInView(true);
                        if (elem && runOnce) observer.unobserve(elem);
                    } else {
                        setIsInView(false);
                    }
                });
            },
            {
                root,
                rootMargin,
                threshold,
            },
        );
        if (elem) observer.observe(elem);
        return () => {
            if (elem) observer.unobserve(elem);
        };
    }, [runOnce, hasBeenInView, root, rootMargin, threshold]);

    return ref;
};

export default useRunOnVisible;
